import { useState } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from '../../../components/application/defaultComponents'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import { useApplication } from 'src/context/application'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import { ReviewData, InsuranceData, ClaimData } from 'src/dtos/applicationData'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { TbFileCheck } from 'react-icons/tb'
import { numberToCurrency } from 'src/utils/numberFormat'
import {FILE_SIZE_LABEL} from "../../../utils/constants";

const Insurance = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { application, saveApplication, updateApplication } = useApplication()
  const [review, setReview] = useState<ReviewData>(application?.liabilitySection?.review ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!application?.liabilitySection?.review?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!application?.liabilitySection?.review?.files)

  const handleEditClick = () => {
    navigate({
      pathname: '/insurance/last-insurances',
      search: createSearchParams({ review: 'insurance' }).toString()
    })
  }

  const updatedApplication = () => ({
    ...application,
    stoppedAt: pathname,
    liabilitySection: {
      ...application?.liabilitySection,
      review
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveApplication(updatedApplication())
    } catch (_) { }
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...application,
    stoppedAt: pathname,
    liabilitySection: {
      ...application?.liabilitySection,
      review: {
        ...application?.liabilitySection?.review,
        [key]: value
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveApplication(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveApplication(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveApplication(applicationFileAttached)
    }
  }

  const insuranceSection = application?.liabilitySection
  const claims = application?.liabilitySection?.pastLiability?.claims

  const hasClaims = insuranceSection?.pastLiability?.hasMalpracticeClaim === true ||
    insuranceSection?.pastLiability?.hasMalpracticeBasis === true

  const willRenderClaims = hasClaims && insuranceSection.pastLiability?.claims && insuranceSection.pastLiability?.claims.length > 0

  const InsuranceComponent = ({ insurance, index }: { insurance: InsuranceData, index: number }) => (
    <section>
      <article>
        <strong>{index === 0 ? 'Most Recent Insurance' : 'Previous Insurance'}</strong>
        <span>
          <h6 style={{ marginTop: 0 }}>Insurance Company: </h6>
          {insurance?.companyName ?? 'N/A'}
        </span>
        <span>
          <h6>Limits of Liability: </h6>
          {insurance?.liabilityLimits ?? 'N/A'}
        </span>
        <span>
          <h6>Deductible: </h6>
          {insurance?.deductible ? numberToCurrency(insurance?.deductible, 0, 0) : 'N/A'}
        </span>
        <span>
          <h6>Expiration Date: </h6>
          {!!insurance?.expirationDate ? insurance?.expirationDate : 'N/A'}
        </span>
        <span>
          <h6>Annual Premium: </h6>
          {insurance?.annualPremium ? numberToCurrency(insurance?.annualPremium, 0, 0) : 'N/A'}
        </span>
        <span>
          <h6>Did you purchase tail coverage for this policy? </h6>
          {handleYesNoAnswers(insurance?.isTailCoverage)}
        </span>
        <span>
          <h6>Dates of Tail Coverage:</h6>
          {(!!insurance?.tailCoverageFromDate && !!insurance?.tailCoverageToDate) ? (
            <>{insurance?.tailCoverageFromDate} to {insurance?.tailCoverageToDate}</>
          ) : (
            <>N/A</>
          )}
        </span>
        {index === 0 ? (
          <>
            <span>
              <h6>Is this your current insurance? </h6>
              {handleYesNoAnswers(insurance?.isCurrent)}
            </span>
            <span>
              <h6>Retroactive Date for Prior Acts Coverage:</h6>
              {!!insurance?.retroactiveDate ? insurance?.retroactiveDate : "N/A"}
            </span>
            <span>
              <h6>Upload insurance policy info. Please include declarations and endorsements: </h6>
            </span>
            {insurance?.policyFile?.map(file => (
              <span key={file?.id} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                {!!file?.fileName ? <TbFileCheck /> : null}
                {file?.fileName ?? 'N/A'}
              </span>
            ))}
          </>
        ) : null}
      </article>
    </section>
  )

  const ClaimComponent = ({ claim, index }: { claim: ClaimData, index: number }) => (
    <section>
      <h5>CLAIM {index + 1}</h5>
      <article>
        <strong>Firm Name</strong>
        <span>{claim?.firmName ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Individuals of firm involved in the claim</strong>
        {claim?.hasInvolvedMembers === false ? (
          <span>I'm not sure</span>
        ) : claim?.involvedMembers ? claim.involvedMembers.map(member => (
          <span key={member}>{member}</span>
        )) : <span>N/A</span>}
      </article>
      <article>
        <strong>Claimant’s Full Name</strong>
        <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Claim Type</strong>
        <span>{claim?.claimType ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Date of Claim</strong>
        <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Date of Alleged Error</strong>
        <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Is this claim closed?</strong>
        <span>{claim?.isClaimClosed ?? 'N/A'}</span>
        {claim?.isClaimClosed === 'Yes' ? (
          <>
            <span>
              <h6>Indicate how the claim was closed: </h6>
              {claim?.closingType ?? 'N/A'}
            </span>
            <span>
              <h6>Total loss paid including deductibles: </h6>
              {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
            </span>
            <span>
              <h6>Defense cost paid: </h6>
              {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
            </span>
          </>
        ) : claim?.isClaimClosed === 'No' ?? null}
      </article>
      <article>
        <strong>Name of Insurer</strong>
        <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Description of Claim</strong>
        <span>
          <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
        </span>
        <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
        </span>
        <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
        </span>
        <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
        <span>
          <h6>Upload any applicable claim information:</h6>
        </span>
        {claim?.claimFile?.map(file => (
          <span style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            {!!file?.fileName ? <TbFileCheck /> : null}
            {file?.fileName ?? 'N/A'}
          </span>
        ))}
      </article>
    </section>
  )

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review your last insurance information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='insuranceData'>
          <header>
            <div>
              <h4>Last Insurance</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{ padding: '8px 46px' }} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='firm'>
            <div className='column'>
              <section>
                <h5>INSURANCE HISTORY</h5>
                <article>
                  <strong>Has firm been insured in the past 5 years?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.isFirmRecentlyInsured)}</span>
                </article>
              </section>
            </div>
          </main>
          {insuranceSection?.isFirmRecentlyInsured === true ? (
            <>
              <h5 style={{marginTop: '32px'}}>PREVIOUS INSURANCES</h5>
              <main className='firm'>
                <div className='column'>
                  {insuranceSection?.insurances?.map((insurance, index) => {
                    if (index % 2 === 0) return <InsuranceComponent key={index} insurance={insurance} index={index} />
                    return null
                  })}
                </div>
                <div className='column'>
                  {insuranceSection?.insurances?.map((insurance, index) => {
                    if (index % 2 !== 0) return <InsuranceComponent key={index} insurance={insurance} index={index} />
                    return null
                  })}
                </div>
              </main>
            </>
          ) : null}
          <h5 style={{marginTop: '32px'}}>PAST LIABILITY</h5>
          <main className='firm'>
            <div className="column">
              <section>
                <article>
                  <strong>In the past 10 years, has your firm or any lawyer at your firm had liability insurance cancelled, declined, or not renewed?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasCanceledLiabilities)}</span>
                  {insuranceSection?.pastLiability?.hasCanceledLiabilities === true ? 
                    insuranceSection?.pastLiability?.canceledLiabilitiesFile?.map(file => (
                      <span key={file?.id} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        {!!file?.fileName ? <TbFileCheck /> : null}
                        {file?.fileName ?? 'N/A'}
                      </span>
                    )) : null}
                </article>
                <article>
                  <strong>
                    Does any firm member or non-attorney employee of your firm (including predecessor firms) know of any circumstance,
                    act, error, or omission that might form the basis of a legal malpractice claim against your firm? Or any attorney in your firm?
                  </strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasMalpracticeBasis)}</span>
                </article>
                <article>
                  <strong>
                    Has your firm or any lawyer at your firm ever voluntarily accepted discipline or been refused admission to the bar,
                    been reprimanded (privately or publicly), suspended from the practice of law, disbarred, received a probationary license,
                    or otherwise disciplined by any disciplinary or licensing authority?
                  </strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasDisciplinedAction)}</span>
                  {insuranceSection?.pastLiability?.hasDisciplinedAction === true ? (
                    <span>{insuranceSection?.pastLiability?.disciplinedActionDetails ?? 'N/A'}</span>
                  ) : null}
                </article>
              </section>
            </div>
            <div className="column">
              <section>
                <article>
                  <strong>In the past 10 years, has your firm or any lawyer at your firm been the subject of a legal malpractice claim?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasMalpracticeClaim)}</span>
                </article>
                <article>
                  <strong>Has your firm or any lawyer at your firm been the subject of a grievance complain in the past five years?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasGrievanceComplain)}</span>
                  {insuranceSection?.pastLiability?.hasGrievanceComplain === true ? 
                    insuranceSection?.pastLiability?.grievanceComplainFile?.map(file => (
                      <span key={file?.id} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        {!!file?.fileName ? <TbFileCheck /> : null}
                        {file?.fileName ?? 'N/A'}
                      </span>
                    )) : null}
                </article>
              </section>
            </div>
          </main>
          {(willRenderClaims === true) ? (
            <main className="firm">
              <div className="column">
                {claims?.map((claim, index) => {
                  if(index % 2 === 0) return <ClaimComponent claim={claim} index={index} key={index} />
                  return null
                })}
              </div>
              <div className="column">
                {claims?.map((claim, index) => {
                  if(index % 2 !== 0) return <ClaimComponent claim={claim} index={index} key={index} />
                  return null
                })}
              </div>
            </main>
          ) : null}
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!insuranceSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker 
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={10}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/review/attorneys"
        nextRoute="/sign/signature"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Insurance